import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/es';
import 'dayjs/locale/it';

import useMaintenanceDialog from 'hooks/MaintenanceDialog.hook';
import {
	useCheckAlive,
	useCookies,
	useGetLocales,
	useGetNotifications,
	useGetProfile,
	useGetRoles,
	useLogin,
} from 'hooks/login.hook';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
	CheckSubmitClaims,
	ContactUsPage,
	Dashboard,
	LocationInformationPage,
	NewClaim,
	ProviderManual,
	ViewClaimDetails,
	ViewClaims,
	ViewPaymentDetails,
	ViewPaymentHistory,
} from '../pages';

import { AuthenticatedTemplate } from '@azure/msal-react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DocumentVisualizerPopup from 'components/Documentation/DocumentVisualizerPopup';
import SharedLayout from 'components/Layout//SharedLayout/SharedLayout.component';
import DashboardLayout from 'components/Layout/DashboardLayout.component';
import DialogConfirm from 'components/commons/Dialogs/DialogConfirm';
import DialogDeleteClaim from 'components/commons/Dialogs/deleteClaim/DialogDeleteClaim';
import DialogDownloadOther from 'components/commons/Dialogs/downloadOther/DialogDownloadOther';
import DialogLocation from 'components/commons/Dialogs/location/DialogLocation';
import DialogSubmitClaim from 'components/commons/Dialogs/submitClaim/DialogSubmitClaim';
import dayjs from 'dayjs';
import CheckMemberDetail from 'pages/CheckMemberDetail';
import ClaimsNotificationsPage from 'pages/ClaimsNotifications.page';
import NotFound from 'pages/NotFound.page';
import ViewPayment from 'pages/ViewPayment';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { LANGUAGES } from 'types/Localization';
import { PATHNAMES } from 'utils/constants';
import { getLanguageStorage } from 'utils/localStorage';
import { ProtectedRoute } from './ProtectedRoute';

interface Props {
	className?: string;
}

export const Router: React.FC<Props> = () => {
	const location = useLocation();
	const [dateJsStarted, setDatejsStarted] = useState(false);
	const { profile } = useSelector((store: RootState) => store.user);

	//login
	useLogin();

	// check connection
	useCheckAlive();

	// get locales
	useGetLocales();

	//get error messages
	useGetRoles();

	//cookies
	useCookies();

	// if the user is logged then lets load his informations
	useGetProfile();

	// maintenance dialog
	useMaintenanceDialog();

	// get notifications
	useGetNotifications(location.pathname);

	useEffect(() => {
		if (location.pathname !== '/contact-us') {
			const captcha = document.getElementsByClassName('grecaptcha-badge')[0];
			if (captcha) captcha.remove();
		}
	}, [location]);

	const currentLanguage = LANGUAGES.find(
		el => el.id.toLowerCase() === getLanguageStorage(),
	);

	useEffect(() => {
		dayjs.locale(currentLanguage?.localeDayjs);
		setDatejsStarted(true);
	}, [currentLanguage]);

	return !dateJsStarted ? null : (
		<AuthenticatedTemplate>
			<LocalizationProvider
				dateAdapter={AdapterDayjs}
				adapterLocale={currentLanguage ? currentLanguage.localeDayjs : 'en'}
			>
				<DialogLocation />
				<DialogDeleteClaim />
				<DialogSubmitClaim />
				<DialogDownloadOther />
				<DialogConfirm />
				<DocumentVisualizerPopup />
				{profile && (
					<Routes>
						<Route path="/not-found" element={<NotFound />} />
						<Route path="/" element={<DashboardLayout />}>
							<Route path="" element={<Dashboard />} />
						</Route>
						<Route path="/" element={<SharedLayout />}>
							<Route
								path={PATHNAMES.CHECK_AND_SUBMIT}
								element={
									<ProtectedRoute
										component={<CheckSubmitClaims />}
										permissionsRequired={['check-eligibility']}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.CHECK_AND_SUBMIT}/:subscriptionUuid`}
								element={
									<ProtectedRoute
										component={<CheckMemberDetail />}
										permissionsRequired={['check-eligibility']}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.CHECK_AND_SUBMIT}/:subscriptionUuid/${PATHNAMES.NEW_CLAIM}`}
								element={
									<ProtectedRoute
										component={<NewClaim />}
										permissionsRequired={['submit-claims']}
									/>
								}
							/>
							<Route
								path={PATHNAMES.VIEW_CLAIMS}
								element={
									<ProtectedRoute
										component={<ViewClaims />}
										permissionsRequired={['view-claims-by-stores']}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.VIEW_CLAIMS}/:Uuid`}
								element={
									<ProtectedRoute
										component={<ViewClaimDetails />}
										permissionsRequired={['view-claims-by-stores']}
									/>
								}
							/>
							<Route
								path={PATHNAMES.LOCATION_INFORMATION}
								element={
									<ProtectedRoute
										component={<LocationInformationPage />}
										permissionsRequired={['location-information-management']}
									/>
								}
							/>
							<Route
								path={PATHNAMES.VIEW_PAYMENT_HISTORY}
								element={
									<ProtectedRoute
										component={<ViewPaymentHistory />}
										permissionsRequired={[
											'view-payments-and-invoices',
											'view-all-payments-for-provider-master-and-invoices',
										]}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.VIEW_PAYMENT_HISTORY}/:id`}
								element={
									<ProtectedRoute
										component={<ViewPayment />}
										permissionsRequired={[
											'view-payments-and-invoices',
											'view-all-payments-for-provider-master-and-invoices',
										]}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.VIEW_PAYMENT_HISTORY}/:id/${PATHNAMES.EXPLANATION_OF_PAYMENT}`}
								element={
									<ProtectedRoute
										component={<ViewPaymentDetails />}
										permissionsRequired={[
											'view-payments-and-invoices',
											'view-all-payments-for-provider-master-and-invoices',
										]}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.VIEW_PAYMENT_HISTORY}/:id/${PATHNAMES.GROUP_INVOICE_ASSESSMENT}`}
								element={
									<ProtectedRoute
										component={<ViewPaymentDetails />}
										permissionsRequired={[
											'view-payments-and-invoices',
											'view-all-payments-for-provider-master-and-invoices',
										]}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.VIEW_PAYMENT_HISTORY}/:id/${PATHNAMES.ASSESSMENT_FEE_INVOICE}/:claimUUID`}
								element={
									<ProtectedRoute
										component={<ViewPaymentDetails />}
										permissionsRequired={[
											'view-payments-and-invoices',
											'view-all-payments-for-provider-master-and-invoices',
										]}
									/>
								}
							/>
							<Route
								path={PATHNAMES.DOCUMENTATION}
								element={
									<ProtectedRoute
										component={<ProviderManual />}
										permissionsRequired={['provider-manual']}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.DOCUMENTATION}/:typology`}
								element={
									<ProtectedRoute
										component={<ProviderManual />}
										permissionsRequired={['provider-manual']}
									/>
								}
							/>
							<Route
								path={PATHNAMES.CONTACT_US}
								element={
									<ProtectedRoute
										component={<ContactUsPage />}
										permissionsRequired={['contact-us']}
									/>
								}
							/>
							<Route
								path={PATHNAMES.CLAIMS_NOTIFICATIONS}
								element={
									<ProtectedRoute
										component={<ClaimsNotificationsPage />}
										permissionsRequired={['view-claims-by-stores']}
									/>
								}
							/>
							<Route
								path={`${PATHNAMES.CLAIMS_NOTIFICATIONS}/:Uuid`}
								element={
									<ProtectedRoute
										component={<ViewClaimDetails />}
										permissionsRequired={['view-claims-by-stores']}
									/>
								}
							/>
						</Route>
						<Route path="*" element={<Navigate to="/not-found" />} />
					</Routes>
				)}
			</LocalizationProvider>
		</AuthenticatedTemplate>
	);
};
